import { pick } from "lodash";
import { MapMouseEvent, PointLike } from "mapbox-gl";
import { useEffect } from "react";

import { useGaiaMap } from "../../hooks/useGaiaMap";
import { parseStringifiedProperties } from "./parseStringifiedProperties";

type GaiaMapFeatureClickListenerProps = {
    onClick: (feature: GeoJSON.Feature | undefined, layerId?: string) => void;
    layerIds: string[];
};

export default function GaiaMapFeatureClickListener({ onClick, layerIds }: GaiaMapFeatureClickListenerProps) {
    const { map } = useGaiaMap();

    useEffect(() => {
        if (!map) return;

        function handler(e: MapMouseEvent) {
            if (!map) return;
            const bbox: [PointLike, PointLike] = [
                [e.point.x - 5, e.point.y - 5],
                [e.point.x + 5, e.point.y + 5],
            ];

            const [feature] = map.queryRenderedFeatures(bbox, {
                layers: layerIds,
            });

            if (feature?.properties) {
                feature.properties = parseStringifiedProperties(feature.properties);
            }

            if (feature) {
                onClick(pick(feature, ["id", "type", "bbox", "geometry", "properties"]), feature.layer?.id);
            } else {
                onClick(undefined);
            }
        }

        function onHover() {
            if (!map) return;
            map.getCanvas().style.cursor = "pointer";
        }

        function onLeave() {
            if (!map) return;
            map.getCanvas().style.cursor = "";
        }

        map.on("click", handler);

        layerIds.map(layerId => {
            map.on("mousemove", layerId, onHover);
            map.on("mouseleave", layerId, onLeave);
        });

        return () => {
            if (!map) return;

            layerIds.map(layerId => {
                map.off("mousemove", layerId, onHover);
                map.off("mouseleave", layerId, onLeave);
            });

            map.off("click", handler);
        };
    }, [map, onClick, layerIds]);

    return null;
}
